/**
 * DEMA-WMA Gainer trading panel: run / pause / kill-switch controls, today's
 * P&L, per-index last evaluated bar, open positions, trade book, signals and
 * order events. Shows LIVE figures when live trading is approved (or live
 * positions are open), otherwise PAPER (simulated).
 */
import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  MenuItem,
} from "@mui/material";
import {
  KILL_CONFIRMATION,
  type DemaWmaConditionCheck,
  type DemaWmaDashboard,
  type DemaWmaPositionView,
  type DemaWmaSignalView,
} from "@/services/demaWmaGainerService";
import { useDemaWmaService } from "@/services/demaWmaServiceContext";
import { extractErrorMessage } from "@/utils/errors";

const num = (v: number | null | undefined, d = 2) => (v === null || v === undefined ? "—" : v.toLocaleString("en-IN", { maximumFractionDigits: d }));
const money = (v: number | null | undefined) => (v === null || v === undefined ? "—" : `₹${num(v)}`);
const time = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
const SHORT: Record<string, string> = {
  "Gap DEMA5-DEMA100": "Gap",
  "Gap DEMA100-DEMA5": "Gap",
  "DEMA100 slope": "DEMA100 slope",
  "Close vs DEMA100": "Close",
  "WMA21 of RSI slope": "RSI-WMA",
};

/** One side's entry conditions as pass / fail chips (value and rule in the tooltip and label). */
function ConditionChecks({ side, checks, all }: { side: "CE" | "PE"; checks: DemaWmaConditionCheck[]; all: boolean }) {
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center" data-testid={`checks-${side}`}>
      <Chip size="small" label={side} color={all ? "success" : "default"} variant={all ? "filled" : "outlined"} />
      {checks.map((c) => (
        <Chip
          key={c.name}
          size="small"
          variant="outlined"
          color={c.passed ? "success" : "error"}
          title={`${c.name}: ${c.value ?? "—"} (needs ${c.rule})`}
          label={`${c.passed ? "✓" : "✗"} ${SHORT[c.name] ?? c.name} ${c.value === null || c.value === undefined ? "—" : num(c.value)} ${c.rule}`}
        />
      ))}
    </Stack>
  );
}

/** Seconds from the candle's END (start + 1 minute) to when TekInvest wrote the signal row. */
const evalSeconds = (s: Pick<DemaWmaSignalView, "bar_start" | "written_at">): string => {
  if (!s.bar_start || !s.written_at) return "—";
  const secs = (new Date(s.written_at).getTime() - (new Date(s.bar_start).getTime() + 60_000)) / 1000;
  return Number.isFinite(secs) ? `${secs.toFixed(1)} s` : "—";
};

const BLOCK_TEXT: Record<string, string> = {
  DAILY_LOSS_LIMIT: "daily loss limit reached",
  LIVE_DAILY_LOSS_LIMIT: "live daily loss limit reached",
  REVISED_RULES_NOT_APPROVED_FOR_LIVE: "this version does not allow live entries",
  ACTIVATION_STOPPED: "activation stopped",
  STRATEGY_DISABLED: "strategy stopped",
  ENTRIES_PAUSED: "entries paused",
  LIVE_EXITS_ONLY: "live exits only",
};

const tone = (v: number | null | undefined) => (v === null || v === undefined ? undefined : v >= 0 ? "success.main" : "error.main");

function Metric({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, height: "100%" }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h6" sx={{ color }}>{value}</Typography>
    </Paper>
  );
}

function PositionsTable({ rows, closed, showOwner = false }: { rows: DemaWmaPositionView[]; closed: boolean; showOwner?: boolean }) {
  if (rows.length === 0) return <Typography variant="body2" color="text.secondary">None.</Typography>;
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {showOwner && <TableCell>Owner</TableCell>}
            <TableCell>Index</TableCell><TableCell>Contract</TableCell><TableCell>Qty</TableCell><TableCell>Entry</TableCell>
            <TableCell>Entry time</TableCell><TableCell>SL / Target</TableCell>
            {closed ? (<><TableCell>Exit</TableCell><TableCell>Reason</TableCell><TableCell>Gross</TableCell><TableCell>Charges</TableCell><TableCell>Net</TableCell></>)
              : (<><TableCell>Last mark</TableCell><TableCell>Unrealized</TableCell></>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((p) => (
            <TableRow key={p.id}>
              {showOwner && <TableCell>{p.owner === "PLATFORM" ? "This page (platform)" : p.owner ?? "—"}</TableCell>}
              <TableCell>{p.index} <Chip size="small" label={p.mode || "PAPER"} color={p.mode === "LIVE" ? "error" : "default"} sx={{ ml: 0.5, height: 18 }} /></TableCell>
              <TableCell>{p.option_type} {num(p.strike, 0)} {p.expiry_date}</TableCell>
              <TableCell>{p.quantity}</TableCell>
              <TableCell>{num(p.entry_price)}</TableCell>
              <TableCell>{time(p.entry_time)}</TableCell>
              <TableCell>{num(p.stop_loss)} / {num(p.target)}</TableCell>
              {closed ? (
                <>
                  <TableCell>{num(p.exit_price)} · {time(p.exit_time)}</TableCell>
                  <TableCell>
                    {p.exit_reason ?? "—"}
                    {p.exit_fallback && (
                      <Chip size="small" color="warning" sx={{ ml: 1 }} label={`stored price ${Math.round(p.exit_fallback.age_seconds)}s old`}
                        title={`No live quote (${p.exit_fallback.live_reject}); filled at ${p.exit_fallback.source}`} />
                    )}
                    {p.manual_reconciliation && <Chip size="small" variant="outlined" sx={{ ml: 1 }} label="manual reconciliation" />}
                  </TableCell>
                  <TableCell sx={{ color: tone(p.gross_pnl) }}>{money(p.gross_pnl)}</TableCell>
                  <TableCell>{money(p.charges)}</TableCell>
                  <TableCell sx={{ color: tone(p.net_pnl), fontWeight: 600 }}>{money(p.net_pnl)}</TableCell>
                </>
              ) : (
                <>
                  <TableCell>
                    {num(p.last_mark)}{p.kill_requested && <Chip size="small" color="error" label="exit requested" sx={{ ml: 1 }} />}
                    {p.pending_exit && (
                      <Chip size="small" color="error" sx={{ ml: 1 }} data-testid="exit-pending"
                        label={`exit pending · ${p.pending_exit.attempts ?? 0} attempt(s)`}
                        title={`${p.pending_exit.reason ?? "exit"} signal not filled yet: ${p.pending_exit.last_error ?? "no quote"}`} />
                    )}
                  </TableCell>
                  <TableCell sx={{ color: tone(p.unrealized_pnl) }}>{money(p.unrealized_pnl)}</TableCell>
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function DemaWmaPaperPanel({ onChanged }: { onChanged?: () => void }) {
  const demaWmaGainerService = useDemaWmaService();
  const [d, setD] = useState<DemaWmaDashboard | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<"open" | "trades" | "signals" | "orders" | "customers">("open");
  const [bookOwner, setBookOwner] = useState("");
  const [bookMode, setBookMode] = useState<"" | "PAPER" | "LIVE">("");
  const [killOpen, setKillOpen] = useState(false);
  const todayIst = new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
  const [csvFrom, setCsvFrom] = useState(todayIst);
  const [csvTo, setCsvTo] = useState(todayIst);
  const [csvMode, setCsvMode] = useState<"" | "PAPER" | "LIVE">("");
  const [csvBusy, setCsvBusy] = useState(false);
  const downloadCsv = async () => {
    setCsvBusy(true);
    setErr(null);
    try {
      await demaWmaGainerService.downloadTradesCsv(csvFrom, csvTo, csvMode || undefined);
    } catch (e) {
      setErr(extractErrorMessage(e));
    } finally {
      setCsvBusy(false);
    }
  };
  const [phrase, setPhrase] = useState("");

  const load = useCallback(async () => {
    try {
      setD(await demaWmaGainerService.getDashboard());
      setErr(null);
    } catch (e) {
      setErr(extractErrorMessage(e));
    }
  }, [demaWmaGainerService]);

  useEffect(() => {
    void load();
    const id = globalThis.setInterval(() => void load(), 30000);
    return () => globalThis.clearInterval(id);
  }, [load]);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      await load();
      onChanged?.();
    } catch (e) {
      setErr(extractErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!d) return err ? <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert> : null;
  // Platform + every customer, paper + live when the backend provides them; this page's own rows otherwise.
  const openRows = d.all_open_positions ?? d.open_positions;
  const allBook = d.trade_book ?? d.trades;
  const bookOwners = Array.from(new Set(allBook.map((p) => p.owner ?? "PLATFORM")));
  const bookRows = allBook.filter((p) => (!bookOwner || (p.owner ?? "PLATFORM") === bookOwner) && (!bookMode || p.mode === bookMode));
  const running = d.execution_status === "LIVE";

  return (
    <Card variant="outlined" sx={{ mb: 2 }} data-testid="paper-panel">
      <CardContent>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }} sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1 }}>
            {d.mode === "LIVE" ? "Live trading" : "Paper trading"}{" "}
            {d.mode === "LIVE"
              ? d.live_orders_will_be_sent
                ? <Chip size="small" color="error" label="LIVE — real broker orders" sx={{ ml: 1 }} />
                : <Chip size="small" color="warning" label="LIVE approved — NOT trading: a live gate is off" sx={{ ml: 1 }} />
              : <Chip size="small" color="info" label="PAPER — simulated, no broker orders" sx={{ ml: 1 }} />}
          </Typography>
          <Chip label={running ? "Running" : "Stopped"} color={running ? "success" : "default"} />
          {d.entries_paused && <Chip label="Entries paused" color="warning" />}
          <Button variant={running ? "outlined" : "contained"} disabled={busy}
            onClick={() => void act(() => demaWmaGainerService.setRunning(!running))}>
            {running ? "Stop" : d.mode === "LIVE" ? "Start" : "Start paper trading"}
          </Button>
          <Button variant="outlined" disabled={busy} onClick={() => void act(() => demaWmaGainerService.setPaused(!d.entries_paused))}>
            {d.entries_paused ? "Resume entries" : "Pause entries"}
          </Button>
          <Button variant="contained" color="error" disabled={busy} onClick={() => { setPhrase(""); setKillOpen(true); }}>
            Kill switch
          </Button>
        </Stack>
        {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>{err}</Alert>}
        {(d.entry_blocks?.length ?? 0) > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }} data-testid="entry-blocks">
            <Typography variant="body2" fontWeight={600}>New entries are being blocked today:</Typography>
            {d.entry_blocks!.map((b, i) => (
              <Typography key={i} variant="body2">
                {b.owner === "PLATFORM" ? "This page (platform)" : b.owner}{b.index ? ` · ${b.index}` : ""}: {BLOCK_TEXT[b.code] ?? b.code}
                {" "}({b.reason}) — last at {time(b.at)}
              </Typography>
            ))}
          </Alert>
        )}
        {d.breeze?.limit_exceeded_at && (
          <Alert severity="error" sx={{ mb: 2 }} data-testid="breeze-limit">
            Breeze daily API limit reached at {time(d.breeze.limit_exceeded_at)} after {d.breeze.calls_today} REST calls: live option
            prices are unavailable until it resets. Exits use the latest stored price (marked on the trade).
          </Alert>
        )}
        {d.open_positions.some((p) => p.pending_exit) && (
          <Alert severity="warning" sx={{ mb: 2 }} data-testid="exit-pending-alert">
            An exit signal has not been filled yet (no usable option quote) — see “exit pending” on the open position.
          </Alert>
        )}

        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={6} md={2}><Metric label="Entries today" value={String(d.today.entries)} /></Grid>
          <Grid item xs={6} md={2}><Metric label="Open" value={String(d.today.open)} /></Grid>
          <Grid item xs={6} md={2}><Metric label="Gross P&L (closed)" value={money(d.today.gross_pnl)} color={tone(d.today.gross_pnl)} /></Grid>
          <Grid item xs={6} md={2}><Metric label="Charges" value={money(d.today.charges)} /></Grid>
          <Grid item xs={6} md={2}><Metric label="Net P&L (closed)" value={money(d.today.net_pnl)} color={tone(d.today.net_pnl)} /></Grid>
          <Grid item xs={6} md={2}><Metric label="Unrealized" value={money(d.today.unrealized_pnl)} color={tone(d.today.unrealized_pnl)} /></Grid>
        </Grid>

        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {["BANKNIFTY", "NIFTY"].map((idx) => {
            const s = d.indices[idx];
            const v = s?.values ?? {};
            return (
              <Grid item xs={12} md={6} key={idx}>
                <Paper variant="outlined" sx={{ p: 1.5 }} data-testid={`index-${idx}`}>
                  <Typography variant="subtitle2">{idx}</Typography>
                  {s ? (
                    <>
                      <Typography variant="body2">
                        Last bar {time(s.bar_start)} · <strong>{s.decision}</strong> {s.side ?? ""} · {s.suppressed_by ?? s.reason}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        DEMA fast {num(v.dema_fast as number)} / slow {num(v.dema_slow as number)} · WMA21 {num(v.wma as number)} (prev {num(v.wma_prev as number)}) ·
                        state {s.state}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No bar evaluated yet (strategy stopped, index not configured, or this index&apos;s feed is not running).</Typography>
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
          <Tab value="open" label={`Open positions (${openRows.length})`} />
          <Tab value="trades" label={`Trade book (${bookRows.length})`} />
          <Tab value="signals" label="Signals" />
          <Tab value="orders" label="Order events" />
          <Tab value="customers" label={`Customer activations (${(d.customers ?? []).length})`} />
        </Tabs>
        {tab === "open" && <PositionsTable rows={openRows} closed={false} showOwner={!!d.all_open_positions} />}
        {tab === "trades" && (
          <>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} sx={{ mb: 1.5 }}
              data-testid="trades-csv">
              <TextField size="small" type="date" label="From" value={csvFrom} onChange={(e) => setCsvFrom(e.target.value)}
                InputLabelProps={{ shrink: true }} />
              <TextField size="small" type="date" label="To" value={csvTo} onChange={(e) => setCsvTo(e.target.value)}
                InputLabelProps={{ shrink: true }} />
              <TextField size="small" select label="Mode" value={csvMode} sx={{ minWidth: 130 }}
                onChange={(e) => setCsvMode(e.target.value as "" | "PAPER" | "LIVE")}>
                <MenuItem value="">Paper + Live</MenuItem>
                <MenuItem value="PAPER">Paper only</MenuItem>
                <MenuItem value="LIVE">Live only</MenuItem>
              </TextField>
              <Button variant="outlined" disabled={csvBusy || !csvFrom || !csvTo} onClick={() => void downloadCsv()}>
                {csvBusy ? "Preparing…" : "Download CSV"}
              </Button>
              <Typography variant="caption" color="text.secondary">
                Every trade entered in the range — this page and every customer — with fills, quotes, slippage, charges and P&amp;L (up to 92 days).
              </Typography>
            </Stack>
            {d.trade_book && (
              <Stack direction="row" spacing={1} sx={{ mb: 1 }} data-testid="book-filters">
                <TextField size="small" select label="Owner" value={bookOwner} sx={{ minWidth: 220 }}
                  onChange={(e) => setBookOwner(e.target.value)}>
                  <MenuItem value="">Everyone</MenuItem>
                  {bookOwners.map((o) => <MenuItem key={o} value={o}>{o === "PLATFORM" ? "This page (platform)" : o}</MenuItem>)}
                </TextField>
                <TextField size="small" select label="Paper / live" value={bookMode} sx={{ minWidth: 150 }}
                  onChange={(e) => setBookMode(e.target.value as "" | "PAPER" | "LIVE")}>
                  <MenuItem value="">Paper + Live</MenuItem>
                  <MenuItem value="PAPER">Paper</MenuItem>
                  <MenuItem value="LIVE">Live</MenuItem>
                </TextField>
              </Stack>
            )}
            <PositionsTable rows={bookRows} closed showOwner={!!d.trade_book} />
          </>
        )}
        {tab === "signals" && (
          <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Bars are labelled by their START (IST): the bar labelled 09:53 completes at 09:54:00, and an entry can only follow
            after that. A trade needs all four conditions of one side on the same completed bar — the chips show which one
            was still missing (e.g. the DEMA100 slope) while the others were already met.
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead><TableRow><TableCell>Bar (start)</TableCell><TableCell>Index</TableCell><TableCell>Decision</TableCell><TableCell>Side</TableCell><TableCell>Reason / block</TableCell><TableCell>Entry conditions (✓ met / ✗ not met)</TableCell><TableCell>Evaluated after close</TableCell><TableCell>DEMA fast / slow</TableCell></TableRow></TableHead>
              <TableBody>
                {d.signals.map((s, i) => (
                  <TableRow key={i}>
                    <TableCell>{time(s.bar_start)}</TableCell><TableCell>{s.index}</TableCell><TableCell>{s.decision}</TableCell>
                    <TableCell>{s.side ?? "—"}</TableCell><TableCell>{s.suppressed_by ?? s.reason}</TableCell>
                    <TableCell sx={{ minWidth: 420 }}>
                      {s.checks ? (
                        <Stack spacing={0.5}>
                          <ConditionChecks side="CE" checks={s.checks.CE} all={s.checks.CE_all} />
                          <ConditionChecks side="PE" checks={s.checks.PE} all={s.checks.PE_all} />
                        </Stack>
                      ) : "—"}
                    </TableCell>
                    <TableCell>{evalSeconds(s)}</TableCell>
                    <TableCell>{num(s.values?.dema_fast as number)} / {num(s.values?.dema_slow as number)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          </>
        )}
        {tab === "orders" && (
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead><TableRow><TableCell>Time</TableCell><TableCell>Intent</TableCell><TableCell>Side</TableCell><TableCell>State</TableCell><TableCell>Contract</TableCell><TableCell>Qty</TableCell><TableCell>Intended</TableCell><TableCell>Limit</TableCell><TableCell>Fill</TableCell><TableCell>Reason</TableCell></TableRow></TableHead>
              <TableBody>
                {d.orders.map((o, i) => (
                  <TableRow key={i}>
                    <TableCell>{time(o.created_at)}</TableCell><TableCell>{o.intent}</TableCell><TableCell>{o.side}</TableCell><TableCell>{o.state}</TableCell>
                    <TableCell>{o.option_type} {num(o.strike, 0)}</TableCell><TableCell>{o.filled_quantity}/{o.quantity}</TableCell>
                    <TableCell>{num(o.intended_price)}</TableCell><TableCell>{num(o.limit_price)}</TableCell><TableCell>{num(o.avg_fill_price)}</TableCell>
                    <TableCell>{o.reason ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {tab === "customers" && (
          (d.customers ?? []).length === 0 ? <Typography variant="body2" color="text.secondary">No customer has activated this strategy.</Typography> : (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow><TableCell>Customer</TableCell><TableCell>Status</TableCell><TableCell>Broker</TableCell><TableCell>Open</TableCell><TableCell>Entries today</TableCell><TableCell>Net today</TableCell></TableRow></TableHead>
                <TableBody>
                  {(d.customers ?? []).map((c) => (
                    <TableRow key={c.user_id}>
                      <TableCell>{c.email}</TableCell>
                      <TableCell>
                        <Chip size="small" label={c.status} color={c.status === "ACTIVE" ? "success" : c.status === "ERROR" ? "error" : "default"} />
                        {c.error_message && <Typography variant="caption" color="error" display="block">{c.error_message}</Typography>}
                      </TableCell>
                      <TableCell>{c.broker ?? "—"}</TableCell>
                      <TableCell>
                        {c.open_positions.length === 0 ? "—" : c.open_positions.map((p) => (
                          <Box key={p.id}>{p.mode} {p.index} {p.option_type} {num(p.strike, 0)} x{p.quantity} @ {num(p.entry_price)}</Box>
                        ))}
                      </TableCell>
                      <TableCell>{c.today.entries}</TableCell>
                      <TableCell sx={{ color: tone(c.today.net_pnl) }}>{money(c.today.net_pnl)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        )}
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Paper fills: buy at the live ask, sell at the live bid (LTP if no bid), each with the configured paper slippage, only inside the limit band.
            Charges are estimates. Refreshes every 30 s.
          </Typography>
        </Box>
      </CardContent>

      <Dialog open={killOpen} onClose={() => setKillOpen(false)}>
        <DialogTitle>Kill switch</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Pauses entries and exits every open paper position on the next minute candle (with a fresh quote). Type <strong>{KILL_CONFIRMATION}</strong> to confirm.
          </Typography>
          <TextField fullWidth size="small" value={phrase} onChange={(e) => setPhrase(e.target.value)} inputProps={{ "aria-label": "confirmation" }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKillOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={phrase.trim() !== KILL_CONFIRMATION || busy}
            onClick={() => void act(async () => { await demaWmaGainerService.killSwitch(phrase.trim()); setKillOpen(false); })}>
            Stop and exit
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

export default DemaWmaPaperPanel;
